<script lang="ts" module>
  import type { BlogPost } from '@agapi/mlc-core';
  import type { RichtextStoryblok } from '@agapi/content-storyblok/storyblok';
  import { sva } from '@agapi/ui/panda/css';

  export const blogCarouselStyles = sva({
    slots: ['section', 'title', 'slide', 'carouselRoot', 'carouselContainer', 'blogCard'],
    base: {
      carouselRoot: {
        overflow: 'visible!'
      },
      carouselContainer: {
        overflow: 'visible!'
      },
      section: {
        py: { base: '3', md: '4' }
      },
      title: {
        fontWeight: 'semibold',
        textAlign: 'center'
      },
      slide: {
        minWidth: { base: '85%', sm: '330px', md: '380px' },
        maxWidth: { base: '85%', sm: '330px', md: '380px' },
        px: '2',
        overflow: 'visible!'
      },
      blogCard: {
        width: '100%',
        _hover: {
          transform: 'scale(1)'
        }
      }
    }
  });

  export interface BlogCarouselProps {
    posts: BlogPost[];
    title?: string;
    hideWhenEmpty?: boolean;
  }
</script>

<script lang="ts">
  import { Carousel, CarouselSlide } from '@agapi/ui/carousel';
  import { Section } from '@agapi/ui/section';
  import { css } from '@agapi/ui/panda/css';
  import { stack } from '@agapi/ui/panda/patterns';

  import BlogCard from '../blog-card/blog-card.svelte';
  import { calculateReadingTime, parseIngress } from '../../../utils/storyblok.client';

  import * as m from '$lib/parachute/messages';

  const { posts, title = m.relatedArticles(), hideWhenEmpty = true }: BlogCarouselProps = $props();

  const shouldShow = !hideWhenEmpty || (posts && posts.length > 0);

  const getBlogCardProps = (post: BlogPost) => {
    return {
      title: post.title,
      description: parseIngress(post.content as RichtextStoryblok[]),
      imageUrl: post.featuredImage?.url,
      imageAlt: post.featuredImage?.alt || post.title,
      publishedAt: post.publishedAt,
      slug: post.slug,
      category: post.categories?.[0]?.name,
      readingTime: calculateReadingTime(post.content as RichtextStoryblok[])
    };
  }

  const classes = blogCarouselStyles.raw();
</script>

{#if shouldShow}
  <Section
    colorVariant="secondary.tonal"
    containerStyles={classes.section}
  >
    <div class={stack({ gap: '6' })}>
      {#if title}
        <h2 class={css(classes.title)}>{title}</h2>
      {/if}

      <Carousel
        options={{
          align: 'start',
          slidesToScroll: 1,
          breakpoints: {
            '(min-width: 768px)': { slidesToScroll: 2 },
            '(min-width: 1024px)': { slidesToScroll: 3 }
          }
        }}
        centerOnOverflow
        hideIndicators
        rootStyles={classes.carouselRoot}
        wrapperStyles={classes.carouselContainer}
        fadeEdges
      >
        {#each posts as post}
          <CarouselSlide css={classes.slide}>
            <BlogCard {...getBlogCardProps(post)} css={classes.blogCard} />
          </CarouselSlide>
        {/each}
      </Carousel>
    </div>
  </Section>
{/if}